import { useEffect, useMemo, useState } from "react";
import io from "socket.io-client";
import { SOCKET_SERVER_URL } from "../constants/socketServer";
import { buildPresenceFromUsersSnapshot } from "../utils/teamPresenceUsersSnapshot";
import { prepareUsersSocketRowsForPresence } from "../utils/teamPresenceUsersSocket";

const EMIT_INTERVAL_MS = 20_000;
const DELTA_FLUSH_MS = 80;

/** Limite de conexões ao servidor de socket (navegador + carga no serviço). */
export const MAX_PLATFORM_PROBE_SOCKETS = 80;

/**
 * Handshakes simultâneos — abrir dezenas de WebSockets de uma vez no mesmo host
 * costuma deixar connected em 0. Depois do handshake a conexão permanece aberta
 * (necessário para ver operadores do Xpro legado e do Xpro 4 no socket).
 */
const MAX_CONCURRENT_HANDSHAKES = 8;
/** Só para a UI (“Conectando…”) — não derruba o socket. */
const SETTLE_UI_MS = 20_000;

/** Id negativo estável por host — não coincide com `usuarios.id` (positivos). Evita o operador real no console aparecer em todas as salas. */
function syntheticProbeUserId(workspaceNorm) {
  const s = String(workspaceNorm || "").toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  if (h === 0) return -1;
  return h > 0 ? -h : h;
}

const PROBE_PRESENCE_NAME = "\u2060";

function normalizeWorkspaceHost(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "";
  return s.replace(/^https?:\/\//, "").split("/")[0].split(":")[0];
}

/**
 * Uma conexão socket.io por workspace (host), mesmo fluxo legado (`newUser` + `users` + eventos de presença).
 * Usa id/nome sintéticos na sondagem para entrar na sala sem registrar o operador humano logado em cada tenant.
 * Handshakes limitados em concorrência; conexões ficam abertas (legado + Xpro 4 no socket).
 */
export function useWorkspaceProbeSockets(workspaceHosts, { bearerToken, userId, enabled }) {
  const [presenceByWorkspace, setPresenceByWorkspace] = useState(() => new Map());
  const [socketStats, setSocketStats] = useState({ connected: 0, total: 0, failed: 0, settled: 0 });

  const hostsFingerprint = useMemo(() => {
    const list = [...new Set(workspaceHosts.map(normalizeWorkspaceHost).filter(Boolean))];
    list.sort();
    return list.slice(0, MAX_PLATFORM_PROBE_SOCKETS).join("\n");
  }, [workspaceHosts]);

  const hostsProbed = useMemo(
    () => (hostsFingerprint ? hostsFingerprint.split("\n").filter(Boolean) : []),
    [hostsFingerprint]
  );

  useEffect(() => {
    if (!enabled || !bearerToken || userId == null || !hostsProbed.length) {
      setPresenceByWorkspace(new Map());
      setSocketStats({ connected: 0, total: 0, failed: 0, settled: 0 });
      return undefined;
    }

    const uid = Number(userId);
    if (!Number.isFinite(uid)) {
      setPresenceByWorkspace(new Map());
      setSocketStats({ connected: 0, total: 0, failed: 0, settled: 0 });
      return undefined;
    }

    let cancelled = false;
    const cleanups = [];
    let connectedCount = 0;
    let failedCount = 0;
    let settledCount = 0;
    let handshakeInFlight = 0;
    let nextIndex = 0;

    const publishStats = () => {
      if (cancelled) return;
      setSocketStats({
        connected: connectedCount,
        total: hostsProbed.length,
        failed: failedCount,
        settled: settledCount,
      });
    };

    setSocketStats({ connected: 0, total: hostsProbed.length, failed: 0, settled: 0 });

    const bumpConnected = (delta) => {
      connectedCount = Math.max(0, connectedCount + delta);
      publishStats();
    };

    const markHandshakeDone = (state, { failed = false } = {}) => {
      if (state.handshakeDone) return;
      state.handshakeDone = true;
      handshakeInFlight = Math.max(0, handshakeInFlight - 1);
      settledCount += 1;
      if (failed) failedCount += 1;
      publishStats();
      pump();
    };

    const startProbe = (workspaceKey) => {
      if (cancelled) return;

      const probeUid = syntheticProbeUserId(workspaceKey);
      // Mesmas opções do SocketContext (auth only) + forceNew para isolar cada sala.
      const socket = io(SOCKET_SERVER_URL, {
        transports: ["websocket"],
        forceNew: true,
        multiplex: false,
        auth: { token: bearerToken },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1_500,
        reconnectionDelayMax: 8_000,
      });

      const presenceRowCache = new Map();
      const state = { handshakeDone: false, countedConnected: false };

      let deltaRef = new Map();
      let flushTimerRef = null;
      let settleTimer = null;

      const isSyntheticProbe = (id) => Number(id) === probeUid;

      const flushDeltas = () => {
        flushTimerRef = null;
        if (!deltaRef.size) return;
        const batch = new Map(deltaRef);
        deltaRef = new Map();
        setPresenceByWorkspace((prev) => {
          const next = new Map(prev);
          const cur = next.get(workspaceKey) || { tabs: new Map(), names: new Map(), detail: new Map() };
          const tabs = new Map(cur.tabs);
          for (const [id, d] of batch) {
            const n = (tabs.get(id) || 0) + d;
            if (n <= 0) tabs.delete(id);
            else tabs.set(id, n);
          }
          next.set(workspaceKey, { tabs, names: cur.names, detail: cur.detail || new Map() });
          return next;
        });
      };

      const queueDelta = (id, d) => {
        deltaRef.set(id, (deltaRef.get(id) || 0) + d);
        if (flushTimerRef == null) {
          flushTimerRef = setTimeout(flushDeltas, DELTA_FLUSH_MS);
        }
      };

      const applyFullList = (payload) => {
        if (flushTimerRef != null) {
          clearTimeout(flushTimerRef);
          flushTimerRef = null;
        }
        deltaRef = new Map();
        if (payload == null) return;
        const enriched = prepareUsersSocketRowsForPresence(payload, presenceRowCache);
        const filtered = enriched.filter((row) => {
          const id = Number(row?.id);
          return Number.isFinite(id) && !isSyntheticProbe(id);
        });
        let counts = new Map();
        let names = new Map();
        let detail = new Map();
        if (filtered.length) {
          const built = buildPresenceFromUsersSnapshot(filtered);
          counts = built.counts;
          names = built.names;
          detail = built.detail;
        }
        setPresenceByWorkspace((prev) => {
          const next = new Map(prev);
          next.set(workspaceKey, { tabs: counts, names, detail });
          return next;
        });
      };

      const emitProbePresence = () => {
        const payload = {
          id: probeUid,
          name: PROBE_PRESENCE_NAME,
          workspace: workspaceKey,
        };
        socket.emit("newUser", payload);
        socket.emit("users", payload);
      };

      const onConnect = () => {
        if (settleTimer != null) {
          clearTimeout(settleTimer);
          settleTimer = null;
        }
        if (!state.countedConnected) {
          state.countedConnected = true;
          bumpConnected(1);
        }
        markHandshakeDone(state);
        emitProbePresence();
      };

      const onDisconnect = () => {
        if (state.countedConnected) {
          state.countedConnected = false;
          bumpConnected(-1);
        }
        presenceRowCache.clear();
      };

      const onUsers = (payload) => applyFullList(payload);

      const onUserOnline = (p) => {
        const id = Number(p?.id);
        if (!Number.isFinite(id) || isSyntheticProbe(id)) return;
        const nm = p?.name != null ? String(p.name).trim() : "";
        if (nm) {
          setPresenceByWorkspace((prev) => {
            const next = new Map(prev);
            const cur = next.get(workspaceKey) || { tabs: new Map(), names: new Map(), detail: new Map() };
            if (cur.names.has(id)) return prev;
            const names = new Map(cur.names);
            names.set(id, nm);
            next.set(workspaceKey, { ...cur, names });
            return next;
          });
        }
        queueDelta(id, 1);
      };

      const onUserOffline = (p) => {
        const id = Number(p?.id);
        if (!Number.isFinite(id) || isSyntheticProbe(id)) return;
        queueDelta(id, -1);
      };

      socket.on("connect", onConnect);
      socket.on("disconnect", onDisconnect);
      socket.on("users", onUsers);
      socket.on("userOnline", onUserOnline);
      socket.on("userOffline", onUserOffline);

      if (socket.connected) {
        onConnect();
      } else {
        // Libera a fila na UI sem derrubar o socket (continua reconectando).
        settleTimer = setTimeout(() => {
          markHandshakeDone(state, { failed: !socket.connected });
        }, SETTLE_UI_MS);
      }

      const emitTick = () => {
        if (!socket.connected) return;
        socket.emit("users", { id: probeUid, name: PROBE_PRESENCE_NAME, workspace: workspaceKey });
      };
      const interval = setInterval(emitTick, EMIT_INTERVAL_MS);

      cleanups.push(() => {
        clearInterval(interval);
        if (settleTimer != null) clearTimeout(settleTimer);
        if (flushTimerRef != null) clearTimeout(flushTimerRef);
        socket.off("connect", onConnect);
        socket.off("disconnect", onDisconnect);
        socket.off("users", onUsers);
        socket.off("userOnline", onUserOnline);
        socket.off("userOffline", onUserOffline);
        socket.disconnect();
      });
    };

    const pump = () => {
      if (cancelled) return;
      while (handshakeInFlight < MAX_CONCURRENT_HANDSHAKES && nextIndex < hostsProbed.length) {
        const workspaceKey = hostsProbed[nextIndex];
        nextIndex += 1;
        handshakeInFlight += 1;
        startProbe(workspaceKey);
      }
    };

    pump();

    return () => {
      cancelled = true;
      for (const fn of cleanups) {
        try {
          fn();
        } catch (_) {
          /* ignore */
        }
      }
      connectedCount = 0;
      failedCount = 0;
      settledCount = 0;
      setSocketStats({ connected: 0, total: 0, failed: 0, settled: 0 });
      setPresenceByWorkspace(new Map());
    };
  }, [hostsProbed, bearerToken, userId, enabled]);

  return { presenceByWorkspace, socketStats, hostsProbed };
}

export { normalizeWorkspaceHost };
